import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Star } from "lucide-react";
import AppStoreButton from "@/components/AppStoreButton";
import { NAV_LINKS } from "@/config";
import { EASE } from "@/components/Reveal";

export function Wordmark({ light = false }) {
  return (
    <Link to="/home" data-testid="brand-wordmark" className="flex items-baseline gap-1.5" aria-label="Five Star ana sayfa">
      <span className={`font-cinzel text-lg sm:text-xl font-semibold tracking-[0.3em] ${light ? "text-ivory-light" : "text-burgundy"}`}>
        FIVE
      </span>
      <Star className="w-3 h-3 fill-gold text-gold self-center" aria-hidden="true" />
      <span className={`font-cinzel text-lg sm:text-xl font-semibold tracking-[0.3em] ${light ? "text-ivory-light" : "text-burgundy"}`}>
        STAR
      </span>
    </Link>
  );
}

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const dark = location.pathname === "/home" && !scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-[70] transition-all duration-500 ${
          scrolled
            ? "bg-ivory-light/85 backdrop-blur-xl border-b border-burgundy/10 shadow-[0_4px_30px_rgba(110,22,51,0.04)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Wordmark light={dark} />
          <nav className="hidden lg:flex items-center gap-9" aria-label="Ana menü">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-[13px] font-sans font-medium tracking-wide transition-colors duration-300 ${
                    isActive
                      ? `${dark ? "text-ivory-light" : "text-burgundy"} border-b border-gold pb-1`
                      : dark
                        ? "text-ivory/75 hover:text-ivory-light"
                        : "text-ink-soft hover:text-burgundy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <AppStoreButton variant={dark ? "light" : "primary"} testid="nav-appstore-button" className="!px-5 !py-2.5 !text-[13px]" />
            </div>
            <button
              type="button"
              data-testid="nav-menu-toggle"
              aria-label={open ? "Menüyü kapat" : "Menüyü aç"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className={`lg:hidden w-11 h-11 flex items-center justify-center ${dark || open ? "text-ivory-light" : "text-burgundy"}`}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-[60] bg-burgundy-deep text-ivory-light flex flex-col pt-28 pb-10 px-8 lg:hidden"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobil menü">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE, delay: 0.08 + i * 0.07 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-${l.testid}`}
                    className="block font-serif text-4xl py-3 border-b border-ivory/10 hover:text-rose-soft transition-colors"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE, delay: 0.5 }}
              className="mt-auto"
            >
              <AppStoreButton variant="light" testid="mobile-appstore-button" className="w-full justify-center" />
              <p className="mt-6 text-xs text-ivory/60 tracking-wide">18+ · Five Star: Concierge Dating</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
